import { PageLoading } from "../../../components/Loading";
import ErrorMessage, { PageErrorContainer } from "../../../components/Error";
import useGetCurrentAttorneyUser from "../../../hooks/useGetCurrentAttorneyUser";
import { useGetStripeAccountStatusQuery } from "../../../api/stripe-account";
import { Header } from "../../../components/typography/Header";
import Layout from "../../../components/layout/attorneys/Layout";
import { formatUserFullName } from "../../../utils/format";
import {
  Paragraph,
  ParagraphHeader,
} from "../../../components/typography/Paragraph";
import styled from "@emotion/styled";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetAttorneyReviewsQuery,
  useGetClientsQuery,
} from "../../../api/attorneys";
import { formatAttorneyReviewRating } from "../../../utils/format";
import AttorneyLeaderboard from "../../../components/layout/attorneys/AttorneyLeaderboard";
import { StyledIconContainer } from "../appointments/AttorneyAppointments";
import Table from "../../../components/Table";
import { COLOR_PAGE_BACKGROUND } from "../../../components/theme";
import { Link } from "react-router";
import { formatAppointmentDate } from "../../../utils/dateUtils";
import AttorneyServiceTag from "../../../components/layout/attorneys/AttorneyServiceTag";
import { IconSize } from "../../../components/theme";
import ChevronRight from "../../../components/icons/ChevronRight";
import NextAppointment from "../../../components/layout/attorneys/NextAppointment";
import { Container } from "../../../components/layout/attorneys/components";
import AttorneyStripeConnectBanner from "../../../components/layout/attorneys/AttorneyStripeConnectBanner";
import useAttorneyDashboardData from "../../../hooks/useAttorneyDashboardData";

const StyledParagraphHeader = styled(ParagraphHeader)`
  margin-top: 32px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const AppointmentsContainer = styled(Container)`
  margin-top: 32px;
`;

const AttorneyDashboard = () => {
  const {
    attorney,
    user,
    isLoading: isAttorneyLoading,
    error: attorneyError,
  } = useGetCurrentAttorneyUser();
  const {
    data: attorneyReviews,
    isLoading: isAttorneyReviewsLoading,
    error: attorneyReviewsError,
  } = useGetAttorneyReviewsQuery(
    attorney
      ? {
          userId: attorney.userId,
          attorneyId: attorney.id,
          status: "APPROVED",
          includeUser: true,
        }
      : skipToken,
  );

  const {
    data: stripeAccountStatus,
    isLoading: isStripeOnboardingLoading,
    error: stripeOnboardingError,
  } = useGetStripeAccountStatusQuery(
    attorney && attorney.lawFirmId
      ? {
          userId: attorney.userId,
          attorneyId: attorney.id,
          lawFirmId: attorney.lawFirmId,
        }
      : skipToken,
  );

  const {
    data: clients,
    isLoading: isClientsLoading,
    error: clientsError,
  } = useGetClientsQuery(
    attorney
      ? {
          userId: attorney.userId,
          attorneyId: attorney.id,
        }
      : skipToken,
    {
      // Poll every 5 minutes for new appointments
      pollingInterval: 300000,
      // Refetch when the window regains focus
      refetchOnFocus: true,
      // Refetch when reconnecting to the internet
      refetchOnReconnect: true,
      // Skip polling when attorney is not available
      skip: !attorney,
    },
  );

  const {
    visibleClientsCount,
    qaCount,
    representationCount,
    todayAppointments,
    thisWeekAppointments,
    nextAppointment,
  } = useAttorneyDashboardData({
    clients: clients ?? [],
    attorneyId: attorney?.id ?? "",
  });

  const isLoading =
    isAttorneyLoading || isAttorneyReviewsLoading || isClientsLoading;
  const error = attorneyError ?? attorneyReviewsError ?? clientsError;

  if (isLoading) {
    return (
      <Layout>
        <PageLoading />
      </Layout>
    );
  }

  if (error || !attorney || !user || !attorneyReviews || !clients) {
    return (
      <Layout>
        <PageErrorContainer>
          <ErrorMessage>
            Error loading attorney dashboard. Please refresh the page and try
            again, if the issue persists, please contact support.
          </ErrorMessage>
        </PageErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header>Hi, {formatUserFullName(user)}!</Header>
      <Paragraph fontWeight={300}>
        {!stripeAccountStatus?.payoutsEnabled
          ? "Welcome to your HelloPrenup dashboard! Here, you'll find all of the details for your services and upcoming appointments. To complete your account setup, connect to Stripe."
          : "Welcome to your HelloPrenup dashboard!"}
      </Paragraph>
      {!stripeAccountStatus?.payoutsEnabled ? (
        <>
          <AttorneyStripeConnectBanner
            isLoading={isStripeOnboardingLoading}
            error={stripeOnboardingError}
            unresolvedRequirements={
              stripeAccountStatus?.unresolvedRequirements ?? []
            }
            isLawFirmLead={!!attorney.isLawFirmLead}
            payoutsEnabled={!!stripeAccountStatus?.payoutsEnabled}
            onConnect={() => {
              if (stripeAccountStatus?.connectUrl) {
                window.location.href = stripeAccountStatus.connectUrl;
              }
            }}
          />
        </>
      ) : (
        <>
          {attorney.isLawFirmLead ? (
            <AttorneyStripeConnectBanner
              isLoading={isStripeOnboardingLoading}
              error={stripeOnboardingError}
              unresolvedRequirements={
                stripeAccountStatus.unresolvedRequirements
              }
              isLawFirmLead={!!attorney.isLawFirmLead}
              payoutsEnabled={!!stripeAccountStatus.payoutsEnabled}
              onConnect={() => {
                if (stripeAccountStatus.connectUrl) {
                  window.location.href = stripeAccountStatus.connectUrl;
                }
              }}
            />
          ) : null}
        </>
      )}
      <StyledParagraphHeader fontWeight={700}>
        Leaderboard
      </StyledParagraphHeader>
      <AttorneyLeaderboard
        clientsCount={visibleClientsCount}
        qaCount={qaCount}
        representationCount={representationCount}
        reviewsRating={formatAttorneyReviewRating(attorneyReviews)}
      />
      {todayAppointments.length > 0 && (
        <AppointmentsContainer>
          <TableContainer>
            <ParagraphHeader fontWeight={700}>Today</ParagraphHeader>
            <Table
              data={todayAppointments}
              columns={[
                {
                  header: "Scheduled Time",
                  accessor: (appointment) => (
                    <Paragraph fontWeight={600}>
                      {formatAppointmentDate(appointment.event, "time")}
                    </Paragraph>
                  ),
                },
                {
                  header: "Client",
                  accessor: (appointment) => (
                    <Paragraph fontWeight={500}>
                      {formatUserFullName(appointment.client, "firstLast")}
                    </Paragraph>
                  ),
                },
                {
                  header: "Service",
                  accessor: (appointment) => (
                    <AttorneyServiceTag
                      service={appointment.purchaseItem.product}
                      color={true}
                      padding={true}
                    />
                  ),
                },
                {
                  header: "",
                  accessor: (appointment) => (
                    <Link
                      to={`/attorney/clients/${appointment.client.userId}/documents/${appointment.client.documentId}`}
                    >
                      <StyledIconContainer
                        width="32px"
                        height="32px"
                        backgroundColor={COLOR_PAGE_BACKGROUND}
                      >
                        <ChevronRight size={IconSize.XS} />
                      </StyledIconContainer>
                    </Link>
                  ),
                },
              ]}
            />
          </TableContainer>
          {nextAppointment && <NextAppointment appointment={nextAppointment} />}
        </AppointmentsContainer>
      )}
      {thisWeekAppointments.length > 0 && (
        <AppointmentsContainer>
          <TableContainer>
            <ParagraphHeader fontWeight={700}>This week</ParagraphHeader>
            <Table
              data={thisWeekAppointments}
              columns={[
                {
                  header: "Scheduled Time",
                  accessor: (appointment) => (
                    <Paragraph fontWeight={600}>
                      {formatAppointmentDate(appointment.event, "short")}
                    </Paragraph>
                  ),
                },
                {
                  header: "Client",
                  accessor: (appointment) => (
                    <Paragraph fontWeight={500}>
                      {formatUserFullName(appointment.client, "firstLast")}
                    </Paragraph>
                  ),
                },
                {
                  header: "Service",
                  accessor: (appointment) => (
                    <AttorneyServiceTag
                      service={appointment.purchaseItem.product}
                      color={true}
                      padding={true}
                    />
                  ),
                },
                {
                  header: "",
                  accessor: (appointment) => (
                    <Link
                      to={`/attorney/clients/${appointment.client.userId}/documents/${appointment.client.documentId}`}
                    >
                      <StyledIconContainer
                        width="32px"
                        height="32px"
                        backgroundColor={COLOR_PAGE_BACKGROUND}
                      >
                        <ChevronRight size={IconSize.XS} />
                      </StyledIconContainer>
                    </Link>
                  ),
                },
              ]}
            />
          </TableContainer>
        </AppointmentsContainer>
      )}
    </Layout>
  );
};

export default AttorneyDashboard;
